// src/store/modules/agentChat/actions.js
import common from '@/config/common';
import authProvider from '@/setup/auth-provider';
import streamAgent from '../services/agentStreamService';
import conversationHistoryApi from '../services/conversationHistoryService';
import formatAssistantMessage from '../utils/messageFormatter';

const auth = authProvider();

const endpointUrl = `${common.discoveryEndPoint()}/v2/ai/agent/stream`;

function conversationSource(conversation) {
  if (conversation.siteChatAgentId) return `agent:${conversation.siteChatAgentId}`;
  return conversation.backend === 'genie' ? 'genie' : 'knowledge-base';
}

function createLocalConversation(source = 'knowledge-base') {
  const now = new Date().toISOString();
  return {
    id: `local-${Date.now()}`,
    title: 'New chat',
    source,
    createdAt: now,
    updatedAt: now,
  };
}

function createUserMessage(text) {
  return {
    id: `user-${Date.now()}-${Math.random()}`,
    role: 'user',
    text,
    createdAt: new Date().toISOString(),
  };
}

function createAssistantMessage() {
  return {
    id: `assistant-${Date.now()}-${Math.random()}`,
    role: 'assistant',
    rawText: '',
    renderedHtml: '',
    format: 'markdown',
    type: 'plain_text',
    payload: null,
    blocks: [],
    status: 'in_progress',
    // toolPending is set to true on tool_call so the UI can show progress while
    // the agent fetches results, and cleared to false when the payload arrives.
    toolPending: false,
    activeToolName: null,
    thinkingStatus: 'Getting started',
    thinkingSteps: ['Getting started'],
    done: false,
    createdAt: new Date().toISOString(),
  };
}

function unwrapCollection(payload) {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.conversations,
    payload?.items,
    payload?.results,
    payload?.value,
    payload?.data,
    payload?.data?.conversations,
    payload?.data?.items,
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
}

function unwrapConversation(payload) {
  if (payload?.conversation) return payload.conversation;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  return payload || {};
}

function pickValue(source, keys) {
  if (!source) return undefined;
  const key = keys.find((candidate) => (
    source[candidate] !== undefined && source[candidate] !== null
  ));
  return key ? source[key] : undefined;
}

function pickString(source, keys) {
  const value = pickValue(source, keys);
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeDate(value) {
  if (!value) return null;
  const normalizedValue = typeof value === 'string'
    && /^\d{4}-\d{2}-\d{2}T/.test(value)
    && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)
    ? `${value}Z`
    : value;
  const date = new Date(normalizedValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function getConversationId(conversation) {
  return pickString(conversation, [
    'id',
    'conversationId',
    'conversationID',
    'conversationGuid',
  ]);
}

function getConversationDate(conversation, fallbackKeys) {
  return normalizeDate(pickValue(conversation, fallbackKeys));
}

function normalizeConversationSummary(conversation) {
  const id = getConversationId(conversation);
  if (!id) return null;

  const createdAt = getConversationDate(conversation, [
    'createdAt',
    'createdOn',
    'createdDate',
    'createdUtc',
    'timestamp',
  ]) || new Date().toISOString();
  const updatedAt = getConversationDate(conversation, [
    'updatedAt',
    'updatedOn',
    'modifiedAt',
    'modifiedOn',
    'lastMessageAt',
    'lastUpdatedAt',
    'lastMessageUtc',
  ]) || createdAt;
  const title = pickString(conversation, [
    'title',
    'name',
    'summary',
    'subject',
    'firstMessage',
  ]);
  const lastMessagePreview = pickString(conversation, ['lastMessagePreview']);

  return {
    id,
    title: title || lastMessagePreview || null,
    lastMessagePreview,
    source: conversationSource(conversation),
    agentDisplayName: conversation.agentDisplayName,
    createdAt,
    updatedAt,
  };
}

function extractMessageText(message) {
  const directText = pickString(message, [
    'text',
    'message',
    'content',
    'body',
    'value',
    'rawText',
  ]);

  if (directText) return directText;

  if (Array.isArray(message?.content)) {
    return message.content
      .map((part) => (typeof part === 'string'
        ? part
        : pickString(part, ['text', 'content', 'value'])))
      .filter(Boolean)
      .join('\n');
  }

  if (message?.content && typeof message.content === 'object') {
    return pickString(message.content, ['text', 'content', 'value']);
  }

  return '';
}

function normalizeMessageRole(message) {
  const roleValue = (
    pickString(message, ['role', 'sender', 'author', 'type'])
    || pickString(message?.author, ['role', 'name'])
    || pickString(message?.sender, ['role', 'name'])
  ).toLowerCase();

  if (roleValue.includes('user') || roleValue.includes('human')) return 'user';
  if (roleValue.includes('tool')) return 'tool';
  return 'assistant';
}

function inferPayloadType(payload) {
  if (!payload || typeof payload !== 'object') return '';

  if (Array.isArray(payload.items) && (
    payload.totalCount !== undefined
    || payload.pageSize !== undefined
    || payload.skip !== undefined
  )) {
    return 'document_list';
  }

  if (payload.documentId || payload.documentID) {
    return 'document_detail';
  }

  return '';
}

function normalizeMessage(message, index) {
  const role = normalizeMessageRole(message);
  if (role === 'tool') return null;

  const createdAt = normalizeDate(pickValue(message, [
    'createdAt',
    'createdOn',
    'createdUtc',
    'timestamp',
    'sentAt',
  ])) || new Date().toISOString();
  const text = extractMessageText(message);
  const id = pickString(message, ['id', 'messageId', 'messageID'])
    || `${role}-${createdAt}-${index}`;

  if (role === 'user') {
    return {
      id,
      role,
      text,
      createdAt,
    };
  }

  const format = pickString(message, ['format', 'formatType', 'contentFormat']) || 'markdown';
  const storedPayload = pickValue(message, ['payload', 'toolPayload', 'toolResult']) || null;
  const nestedPayloadType = pickString(storedPayload, ['payloadType', 'responseType']);
  const payload = storedPayload?.payload && nestedPayloadType
    ? storedPayload.payload
    : storedPayload;
  const messageType = pickString(message, ['payloadType', 'responseType'])
    || nestedPayloadType
    || inferPayloadType(payload);

  return {
    id,
    role: 'assistant',
    rawText: text,
    renderedHtml: formatAssistantMessage(text, format),
    format,
    type: messageType || (payload ? 'tool_result' : 'plain_text'),
    payload,
    blocks: Array.isArray(message.blocks) ? message.blocks : [],
    status: message.status || 'completed',
    toolPending: false,
    activeToolName: null,
    thinkingStatus: null,
    thinkingSteps: [],
    done: true,
    createdAt,
  };
}

function getConversationMessages(conversation) {
  return unwrapCollection({
    conversations: conversation?.messages,
    items: conversation?.history,
    results: conversation?.turns,
    value: conversation?.entries,
    data: conversation?.data?.messages,
  });
}

function normalizeConversationDetail(payload, fallbackId) {
  const conversation = unwrapConversation(payload);
  const normalizedConversation = normalizeConversationSummary({
    ...conversation,
    id: getConversationId(conversation) || fallbackId,
  });
  const messages = getConversationMessages(conversation)
    .map((message, index) => normalizeMessage(message, index))
    .filter(Boolean);
  const firstUserMessage = messages.find((message) => message.role === 'user');
  const conversationSummary = normalizedConversation
    ? {
      ...normalizedConversation,
      title: normalizedConversation.title || firstUserMessage?.text || null,
    }
    : {
      id: fallbackId,
      title: firstUserMessage?.text || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

  return {
    conversation: conversationSummary,
    messages,
  };
}

function normalizeToolPayload(toolName, payload) {
  if (!payload) return null;

  switch (toolName) {
    case 'searchIndex':
      return {
        payloadType: 'search_results',
        payload,
      };

    default:
      return {
        payloadType: 'tool_result',
        payload: {
          toolName,
          ...payload,
        },
      };
  }
}

function getToolThinkingStatus(toolName) {
  const normalizedToolName = (toolName || '').toLowerCase();

  if (normalizedToolName.includes('searchindex')) {
    return 'Searching relevant documents';
  }

  if (normalizedToolName.includes('getdocumentcontent')) {
    return 'Reading the current document';
  }

  return 'Working through the request';
}

function getPayloadThinkingStatus(payloadType) {
  switch (payloadType) {
    case 'document_list':
    case 'search_results':
      return 'Found relevant documents. Writing the answer';

    case 'document_detail':
      return 'Found the document details. Writing the answer';

    default:
      return 'Writing the answer';
  }
}

export default {
  async loadSources({ state, commit }, siteId = null) {
    const requestId = state.sourcesRequestId + 1;
    commit('SET_SOURCE_REQUEST', { siteId, requestId });
    commit('SET_DEFAULT_SOURCE', 'knowledge-base');
    commit('SET_SOURCES_LOADING', true);
    commit('SET_SOURCE_AVAILABILITY', false);
    commit('SET_CHAT_AGENTS', []);
    try {
      const sources = await conversationHistoryApi.fetchSources();
      if (state.sourcesRequestId !== requestId) return false;
      if (siteId && String(sources.siteId) !== String(siteId)) {
        throw new Error('The current site has changed. Reload the page to load its chat sources.');
      }
      commit('SET_DEFAULT_SOURCE', sources.defaultSource || 'knowledge-base');
      commit('SET_SOURCE_AVAILABILITY', sources.genie === true);
      commit('SET_CHAT_AGENTS', Array.isArray(sources.agents) ? sources.agents : []);
      return true;
    } catch (error) {
      if (state.sourcesRequestId !== requestId) return false;
      commit('SET_ERROR', 'Could not load chat agents. Please reload the page to retry.');
      return false;
    } finally {
      if (state.sourcesRequestId === requestId) commit('SET_SOURCES_LOADING', false);
    }
  },

  async selectSource({ state, commit, dispatch }, source) {
    const available = source === 'knowledge-base'
      || state.chatAgents.some((agent) => source === `agent:${agent.siteChatAgentId}`)
      || (source === 'genie' && state.genieAvailable && !state.chatAgents.length);
    if (!available || source === state.selectedSource
      || state.isStreaming || state.isLoadingActiveConversation || state.sourcesLoading) return;
    commit('SET_SELECTED_SOURCE', source);
    await dispatch('startNewConversation');
  },

  async loadConversations({ commit }) {
    commit('SET_LOADING_CONVERSATIONS', true);
    commit('SET_ERROR', null);

    try {
      const response = await conversationHistoryApi.fetchConversations();
      const conversations = unwrapCollection(response)
        .map(normalizeConversationSummary)
        .filter(Boolean)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

      commit('SET_CONVERSATIONS', conversations);
    } catch (error) {
      commit('SET_ERROR', error?.message || 'Failed to load conversations');
    } finally {
      commit('SET_LOADING_CONVERSATIONS', false);
    }
  },

  async startNewConversation({ state, commit, dispatch }) {
    if (state.isStreaming) await dispatch('stopStreaming');
    if (state.isStreaming) return;
    const conversation = createLocalConversation(state.selectedSource);

    commit('ENSURE_CONVERSATION', conversation);
    commit('SET_ACTIVE_CONVERSATION_ID', conversation.id);
    commit('SET_ERROR', null);
  },

  async loadConversation({ commit }, payload) {
    const conversationId = typeof payload === 'string' ? payload : payload?.conversationId;
    if (!conversationId || conversationId.startsWith('local-')) return false;

    commit('SET_LOADING_ACTIVE_CONVERSATION', true);
    commit('SET_ERROR', null);

    try {
      const response = await conversationHistoryApi.fetchConversation(conversationId);
      const normalized = normalizeConversationDetail(response, conversationId);
      commit('SET_CONVERSATION_MESSAGES', normalized);
      const detail = unwrapConversation(response);
      commit('SET_SELECTED_SOURCE', conversationSource(detail));
      commit('SET_ACTIVE_REQUEST', { requestId: detail.activeRequestId, backend: detail.backend || 'starfish' });
      commit('SET_STREAMING', Boolean(detail.activeRequestId));
      if (detail.activeRequestId) {
        commit('SET_ERROR', 'This conversation has an unfinished request. Use Stop to cancel it before continuing.');
      }
      return true;
    } catch (error) {
      commit('SET_ERROR', error?.message || 'Failed to load conversation');
      return false;
    } finally {
      commit('SET_LOADING_ACTIVE_CONVERSATION', false);
    }
  },

  async setActiveConversation({ state, commit, dispatch }, conversationId) {
    if (state.isStreaming) await dispatch('stopStreaming');
    if (state.isStreaming) return false;
    commit('SET_ACTIVE_CONVERSATION_ID', conversationId);
    const selected = state.conversations.find((conversation) => conversation.id === conversationId);
    if (selected?.source) commit('SET_SELECTED_SOURCE', selected.source);
    commit('SET_ERROR', null);

    if (conversationId && !conversationId.startsWith('local-')) {
      return dispatch('loadConversation', { conversationId });
    }
    return true;
  },

  async stopStreaming({ state, commit }) {
    const controller = state.abortController;
    const requestId = state.activeRequestId;
    try {
      if (state.activeBackend === 'genie' && requestId) {
        await conversationHistoryApi.cancelRequest(state.activeConversationId, requestId);
      }
    } catch (error) {
      if (error?.response?.status !== 409) {
        commit('SET_ERROR', 'Cancellation could not be confirmed. Use Stop again or start a new chat.');
        return;
      }
    }
    if (state.abortController === controller) {
      commit('SET_LAST_ASSISTANT_STATUS', { conversationId: state.activeConversationId, status: 'cancelled' });
      if (controller) controller.abort();
      commit('SET_ABORT_CONTROLLER', null);
      commit('SET_ACTIVE_REQUEST', {});
      commit('SET_STREAMING', false);
    }
  },

  async sendMessage({ state, commit }, { userInput, pageContext }) {
    if (!userInput || !userInput.trim() || state.isStreaming || state.isLoadingActiveConversation) return;

    let conversationId = state.activeConversationId;

    if (!conversationId) {
      const localConversation = createLocalConversation(state.selectedSource);
      commit('ENSURE_CONVERSATION', localConversation);
      commit('SET_ACTIVE_CONVERSATION_ID', localConversation.id);
      conversationId = localConversation.id;
    }

    commit('SET_ERROR', null);
    commit('SET_STREAMING', true);
    commit('SET_ACTIVE_REQUEST', { backend: 'starfish' });

    const userMessage = createUserMessage(userInput);
    const assistantMessage = createAssistantMessage();

    commit('ADD_MESSAGE', {
      conversationId,
      message: userMessage,
    });

    commit('ADD_MESSAGE', {
      conversationId,
      message: assistantMessage,
    });

    commit('TOUCH_CONVERSATION', conversationId);

    const controller = new AbortController();
    commit('SET_ABORT_CONTROLLER', controller);

    let resolvedConversationId = conversationId;
    let streamComplete = false;
    let needsRecovery = false;

    try {
      const token = await auth.bearerToken();

      await streamAgent({
        url: endpointUrl,
        token,
        signal: controller.signal,
        payload: {
          message: userInput,
          source: state.selectedSource.startsWith('agent:') ? 'genie' : state.selectedSource,
          ...(state.selectedSource.startsWith('agent:')
            ? { siteChatAgentId: Number(state.selectedSource.slice(6)) } : {}),
          context: {
            preferredResponseFormat: 'markdown',
            ...pageContext,
          },
          conversationId: conversationId.startsWith('local-') ? null : conversationId,
        },
        onEvent: ({ type, data }) => {
          if (state.abortController !== controller) return;
          switch (type) {
            case 'request_started':
              commit('SET_ACTIVE_REQUEST', { requestId: data.requestId, backend: 'genie' });
              break;
            case 'progress':
              commit('SET_ASSISTANT_THINKING_STATUS', { conversationId: resolvedConversationId, messageId: assistantMessage.id, thinkingStatus: data.message });
              break;
            case 'analytics':
              commit('SET_ANALYTICS', { conversationId: resolvedConversationId, messageId: assistantMessage.id, data });
              break;
            // Backend may send either 'conversation_start' (SSE spec) or 'conversation' (legacy).
            case 'conversation_start':
            case 'conversation': {
              if (data?.backend) {
                commit('SET_ACTIVE_REQUEST', { backend: data.backend });
                commit('SET_SELECTED_SOURCE', conversationSource(data));
              }
              const newConversationId = data?.conversationId;
              if (!newConversationId) return;

              commit('ENSURE_CONVERSATION', {
                id: newConversationId,
                title: 'New chat',
              });

              commit('REPLACE_CONVERSATION_ID', {
                oldId: resolvedConversationId,
                newId: newConversationId,
              });

              commit('SET_ACTIVE_CONVERSATION_ID', newConversationId);
              resolvedConversationId = newConversationId;
              break;
            }

            case 'start':
              if (data?.userInput) {
                commit('SET_CONVERSATION_TITLE', {
                  conversationId: resolvedConversationId,
                  title: data.userInput.slice(0, 40),
                });
              }
              break;

            // tool_call signals the agent is working; show compact progress text
            // while the payload and final answer are on their way.
            case 'tool_call':
              commit('SET_ASSISTANT_TOOL_PENDING', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                toolName: data?.toolName || null,
                thinkingStatus: getToolThinkingStatus(data?.toolName),
              });
              break;

            case 'content_start':
              commit('SET_ASSISTANT_FORMAT', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                format: data?.formatType || data?.format || 'markdown',
              });
              commit('SET_ASSISTANT_THINKING_STATUS', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                thinkingStatus: 'Preparing the answer',
              });
              break;

            case 'delta':
              commit('APPEND_ASSISTANT_DELTA', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                text: data?.text || '',
                format: data?.format,
              });
              commit('TOUCH_CONVERSATION', resolvedConversationId);
              break;

            case 'tool_result': {
              if (!data?.success || !data?.toolName || !data?.data) {
                break;
              }

              const normalized = normalizeToolPayload(data.toolName, data.data);

              if (!normalized) {
                break;
              }

              commit('SET_ASSISTANT_PAYLOAD', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                payloadType: normalized.payloadType,
                payload: normalized.payload,
                thinkingStatus: getPayloadThinkingStatus(normalized.payloadType),
              });

              commit('TOUCH_CONVERSATION', resolvedConversationId);
              break;
            }

            case 'payload':
              commit('SET_ASSISTANT_PAYLOAD', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                payloadType: data?.payloadType || 'plain_text',
                payload: data?.payload || null,
                thinkingStatus: getPayloadThinkingStatus(data?.payloadType),
              });
              commit('TOUCH_CONVERSATION', resolvedConversationId);
              break;

            case 'message':
              commit('SET_ASSISTANT_MESSAGE', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
                text: data?.text || '',
                format: data?.format,
              });
              commit('TOUCH_CONVERSATION', resolvedConversationId);
              break;

            case 'complete':
              streamComplete = true;
              needsRecovery = Boolean(data?.needsRecovery);
              commit('MARK_MESSAGE_DONE', {
                conversationId: resolvedConversationId,
                messageId: assistantMessage.id,
              });
              break;

            case 'error':
              commit('SET_ERROR', data?.message || 'Streaming failed');
              break;

            default:
              break;
          }
        },
        onError: (error) => {
          if (state.abortController === controller) commit('SET_ERROR', error?.message || 'Streaming failed');
        },
      });
    } catch (error) {
      if (error.name !== 'AbortError' && state.abortController === controller) {
        commit('SET_ERROR', error?.message || 'Streaming failed');
      }
    } finally {
      commit('MARK_MESSAGE_DONE', {
        conversationId: resolvedConversationId,
        messageId: assistantMessage.id,
      });
      if (state.abortController === controller) {
        const interrupted = !streamComplete && !controller.signal.aborted && state.activeBackend === 'genie' && state.activeRequestId;
        if (interrupted) {
          commit('SET_ERROR', 'The connection ended before completion. Use Stop or reload the conversation to check its status.');
          commit('SET_LAST_ASSISTANT_STATUS', { conversationId: resolvedConversationId, status: 'failed' });
        }
        commit('SET_ABORT_CONTROLLER', null);
        if (!needsRecovery && !interrupted) commit('SET_ACTIVE_REQUEST', {});
        commit('SET_STREAMING', Boolean(needsRecovery || interrupted));
      }
    }
  },
};
